import {
  FluidBreadcrumbConfig,
  FluidBreadcrumbTranslationOptions,
  FluidMenuItem,
  FluidTheme,
  FluidThemeInterface,
  HTMLFluidBreadcrumbElement,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core';

import {
  translateMenu,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  BreadcrumbContextMenu,
  BreadcrumbCurrentPage,
  BreadcrumbHomeLink,
  BreadcrumbTrail,
} from './components/fluid-breadcrumb.components';

/**
 * @displayName Breadcrumb
 *
 * @contextData At the top of a multi-page app, the breadcrumb component allows user to reference
 * the current page and the depth of navigation that has brought them to that current page within
 * the site structure. Usually the breadcrumb trail references previous page titles as well as
 * providing users clickable links to navigate to a previous page, several pages back, and at
 * the homepage. Styles are usually defined to distinguish clickable links versus the current
 * page being rendered.
 * <br/><br/>
 * <strong>IMPORTANT: Clicking on a link within breadcrumb will not generate a
 * new page load. Breadcrumb is written to pass link value as an event detail. Page routing
 * should therefore be handled as an event listener for breadcrumbClicked event.</strong>
 */
@Component({
  tag: 'fluid-breadcrumb',
  styleUrl: 'fluid-breadcrumb.scss',
  shadow: true,
})
export class FluidBreadcrumb
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Breadcrumb "trail" container's width WITHOUT responsive Context Menu
   * (i.e. intrinsic width of all "trail" links + current page indicator).
   * Used to determine whether responsive Context Menu should be hidden
   * or shown.
   */
  private _containerWidth: number;

  /**
   * Boolean flag that triggers an update to "_containerWidth" when
   * "homeLabel" and/or "trail" Props updated. Necessary to trigger
   * update AFTER component re-renders.
   */
  private _updateContainerWidth = false;

  /**
   * Total length of Breadcrumb's navigation items - includes the
   * "home link", trail, & current page.
   */
  private _totalLength = 0;

  /**
   * Reference to ResizeObserver instance that monitors Breadcrumb's
   * overall width & determines whether responsive Context Menu should
   * be rendered.
   */
  private _resizeObserver: ResizeObserver;

  /**
   * Reference to Breadcrumb "trail" container element.
   */
  _containerRef: HTMLElement;

  /**
   * Reference to the embedded Context Menu's container element.
   */
  _contextMenuContainerRef: HTMLElement;

  /**
   * Reference to the Breadcrumb "trail" container element.
   */
  _trailContainerRef: HTMLElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidBreadcrumbElement;

  // ================================================================ //
  // -- States

  /**
   * FluidMenuItem generated from "homeLink" & "homeLabel" Props.
   */
  @State() _homeLink: FluidMenuItem;

  /**
   * Breadcrumb's visible links BETWEEN "home link" & "current page"
   * (i.e. FluidMenuItems from "trail" Prop MINUS the last item).
   */
  @State() _trail: FluidMenuItem[] = [];

  /**
   * The last item in the "trail" Prop that represents the page that the
   * user is currently viewing. This variable will be undefined IF
   */
  @State() _currentPage: FluidMenuItem;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the breadcrumb in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Allows for passing a string to substitute the default text "Home" for
   * the home page or first element of the breadcrumb
   */
  @Prop({ mutable: true }) homeLabel: string;

  /**
   * Allows for passing a custom path to substitute the default root "/" for
   * the home page or first link of the breadcrumb
   */
  @Prop({ mutable: true }) homeLink: string;

  /**
   * Allows for the building of an array of links to trace the trail of the breadcrumb
   * navigation. The history, sequence and shifting of this array should be maintained
   * by the developer as part of the flow of the pages within an application.
   */
  @Prop({ mutable: true }) trail: FluidMenuItem[] = [];

  @Watch('homeLabel')
  @Watch('homeLink')
  @Watch('trail')
  handleTrailUpdate() {
    if (!this._containerRef) return;
    this.updateBreadcrumb();
    this._updateContainerWidth = true;
  }

  /**
   * Allows a single config object to be given, rather than inline properties
   * so config can be passed as a single prop.
   */
  @Prop({ mutable: true }) config: FluidBreadcrumbConfig;

  @Watch('config')
  handleConfigUpdate() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Events

  /**
   * Emits the link route and prevents default behavior of anchor when clicked.
   * In order to handle the routing of the application; this value should be
   * intercepted by adding an event listener to breadcrumb component; routing
   * logic should be handled under here as click will not load a new page.
   */
  @Event() breadcrumbClicked: EventEmitter<FluidMenuItem>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      language: () => {
        this.trail = translateMenu(this.trail, this._i18nStore);
      },
    });
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad(): void {
    this.updateBreadcrumb();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    // Prevents FOUC on first load
    this._containerRef.style.visibility = 'hidden';

    readTask(() => {
      this.setupResizeObserver();

      Utils.nextTick().then(() => {
        this.updateContainerWidth();
        this._containerRef.style.visibility = null;
      });
    });
  }

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {
    if (this._updateContainerWidth) {
      this._updateContainerWidth = false;

      readTask(() => {
        Utils.nextTick().then(() => {
          this.updateContainerWidth();
        });
      });
    }
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
    this._resizeObserver?.disconnect();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Initializes a ResizeObserver to monitor the width of the component.
   * Triggers "updateTrailStructure()" call if "host" width changes due
   * to "trail" updates or changes to the component's parent container /
   * surrounding UI.
   */
  setupResizeObserver(): void {
    this._resizeObserver = new ResizeObserver(
      Utils.debounce((entries: ResizeObserverEntry[]) => {
        const width = entries[0]?.borderBoxSize[0]?.inlineSize;
        this.updateTrailStructure(width);
      }, 50)
    );
    this._resizeObserver.observe(this.host);
  }

  /**
   * Gets total width of Breadcrumb trail container (WITHOUT responsive
   * Context Menu) & assigns value to "_containerWidth" property. Only
   * called when "homeLabel" and/or "trail" Prop(s) initialized or updated.
   */
  updateContainerWidth(): void {
    if (!this._containerRef) return;

    this.toggleContextMenuVisibility(false);
    this._containerWidth = this._containerRef?.offsetWidth;
    this.updateTrailStructure();
  }

  /**
   * Determines if Breadcrumb should display the entire "trail" upfront
   * or if some links should be displayed within a Context Menu to save
   * screen real estate. Context Menu is only displayed if the total width
   * of the "trail" is greater than the width of Breadcrumb's parent
   * container AND there are at least 3+ links shown (including "home").
   * @param hostWidth - current width of the Breadcrumb's host element
   */
  updateTrailStructure(hostWidth: number = this.host.offsetWidth): void {
    if (this._containerWidth >= hostWidth && this._totalLength > 2) {
      this.toggleContextMenuVisibility(true);
    } else {
      this.toggleContextMenuVisibility(false);
    }
  }

  /**
   * Shows or hides the responsive Context Menu using inline "style" attr.
   * If Context Menu is shown, individual "trail" links between the "home
   * link" (or 1st "trail" link if "home" is not provided) & the "current
   * page indicator" are hidden.
   * @param visible - shows Context Menu when true, hides when false
   */
  toggleContextMenuVisibility(visible: boolean): void {
    if (!this._contextMenuContainerRef || !this._trailContainerRef) return;
    this._contextMenuContainerRef.style.display = visible ? null : 'none';
    this._trailContainerRef.style.display = visible ? 'none' : null;
  }

  /**
   * Constructs the home link object for the breadcrumb using either the
   * provided translations or the default values.
   * @param homeLabel - value of the "homeLabel" Prop
   * @param homeLink - value of the "homeLink" Prop
   * @param _i18nStore - i18n store used for translations
   * @param fluidTranslate - i18n translation options
   * @returns - FluidMenuItem object for the "home link"
   */
  getHomeLink(
    homeLabel: string,
    homeLink: string,
    _i18nStore: FluidI18nStore,
    fluidTranslate: FluidBreadcrumbTranslationOptions
  ): FluidMenuItem {
    if (isNullOrUndefined(homeLink) || isNullOrUndefined(homeLabel)) return;
    return {
      route: homeLink,
      label: _i18nStore?.translate(fluidTranslate?.homeLabel) || homeLabel,
    };
  }

  /**
   * Clones menu items & returns a deep copy of the original
   * FluidMenuItem array. Used to initialize internal "_trail" property.
   * @param trail - FluidMenuItem array (typically the "trail" Prop)
   * @returns - deep copy of the "trail" param
   */
  getTrail(trail: FluidMenuItem[]): FluidMenuItem[] {
    return (trail ?? []).map((menuItem) => ({ ...menuItem }));
  }

  /**
   * Updates Breadcrumb's internal state based on the component's current
   * Prop configuration.
   */
  updateBreadcrumb() {
    this._homeLink = this.getHomeLink(
      this.homeLabel,
      this.homeLink,
      this._i18nStore,
      this.fluidTranslate
    );
    this._trail = this.getTrail(this.trail);
    this._totalLength = (this._homeLink ? 1 : 0) + this._trail.length;

    // If "homeLink" Prop not provided, set internal "_homeLink" to 1st item in "trail"
    if (!this._homeLink && this._totalLength > 2) {
      const [firstLink, ...restOfTrail] = this._trail;
      this._homeLink = firstLink;
      this._trail = restOfTrail;
    }

    this._currentPage = this._trail.pop();
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        ref={(el: HTMLElement) => (this._containerRef = el)}
        class={`fluid-breadcrumb theme-${this.theme}`}
        data-test-id={`${this.identifier}-fluid-breadcrumb`}>
        <BreadcrumbHomeLink
          link={this._homeLink}
          trailLength={this.trail.length}
          breadcrumbClicked={this.breadcrumbClicked}
          theme={this.theme}
          identifier={this.identifier}
        />
        <BreadcrumbContextMenu
          trail={this._trail}
          breadcrumbClicked={this.breadcrumbClicked}
          theme={this.theme}
          identifier={this.identifier}
          setRef={(el) => (this._contextMenuContainerRef = el)}
        />
        <BreadcrumbTrail
          trail={this._trail}
          breadcrumbClicked={this.breadcrumbClicked}
          theme={this.theme}
          identifier={this.identifier}
          setRef={(el) => (this._trailContainerRef = el)}
        />
        <BreadcrumbCurrentPage
          link={this._currentPage}
          theme={this.theme}
          identifier={this.identifier}
        />
      </div>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidBreadcrumbTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;
}
